import {Component, HostListener, OnInit} from '@angular/core';
import { Firestore, collection, addDoc, updateDoc, doc, getDoc } from '@angular/fire/firestore';
import { getAuth } from 'firebase/auth';
import { Project } from '../interfaces/project';
import { UserService } from '../services/user.service';
import { Router, ActivatedRoute } from '@angular/router';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import firebase from 'firebase/compat/app';
import {CanComponentDeactivate} from "../unsaved-changes.guard";


@Component({
  selector: 'app-new-project',
  templateUrl: './new-project.component.html',
  styleUrl: './new-project.component.css'
})
export class NewProjectComponent implements OnInit, CanComponentDeactivate {
  currentDraft: Project | null = null;

  constructor(
    private userService: UserService,
    private router: Router,
    private firestore: Firestore,
    private route: ActivatedRoute
  ) {}

  async ngOnInit() {
    // Check if we're loading a draft
    const draftId = this.route.snapshot.queryParamMap.get('draftId');
    if (draftId) {
      await this.loadDraft(draftId);
    }
  }

  async loadDraft(draftId: string) {
    try {
      const docRef = doc(this.firestore, 'projects', draftId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        this.currentDraft = { id: docSnap.id, ...docSnap.data() } as Project;
      }
    } catch (error) {
      console.error('Error loading draft:', error);
    }
  }

  async uploadImages(projectId: string, selectedImages: File[]): Promise<string[]> {
    const storage = getStorage();
    const uploadPromises = selectedImages.map(async (file) => {
      const storageRef = ref(storage, `projects/${projectId}/${file.name}`);
      await uploadBytes(storageRef, file);
      return getDownloadURL(storageRef);
    });

    return Promise.all(uploadPromises);
  }

  // navigates to the project page on submit
  async onProjectSubmit(obj: {formData: Project, selectedImages: File[]}) {
    const formData = obj.formData;
    const selectedImages = obj.selectedImages;
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      return alert("You must be logged in to submit this form.");
    }

    const uid = this.userService.getUID();
    if (!uid) {
      return alert("No UID Found");
    }

    formData.owner = uid;
    console.log('Form Data being sent to Firebase:', formData);
    console.log('Status field value:', formData.status);

    try {
      const formCollection = collection(this.firestore, 'projects');

      // Check if this is an existing draft being updated
      let docRef;
      if (formData.id) {
        // Update existing document
        docRef = doc(this.firestore, 'projects', formData.id);
        await updateDoc(docRef, { ...formData });
        console.log('Project updated with ID:', formData.id);
        console.log('Updated with status:', formData.status);
      } else {
        // Create new document
        docRef = await addDoc(formCollection, formData);
        console.log('Project created with ID:', docRef.id);
        console.log('Created with status:', formData.status);
        formData.id = docRef.id;
      }

      if (selectedImages.length > 0) {
        const imageUrls = await this.uploadImages(formData.id || docRef.id, selectedImages);
        await updateDoc(formData.id ? docRef : doc(this.firestore, 'projects', docRef.id), {
          images: imageUrls
        });
      }

      // Link project to an Annual profile
      const profileId = this.route.snapshot.queryParamMap.get('profileId');
      if (profileId) {
        const profileRef = firebase.firestore().collection('annualSchoolProfile').doc(profileId);
        const projectRef = firebase.firestore().doc(`/projects/${docRef.id}`);

        await profileRef.update({
          project: firebase.firestore.FieldValue.arrayUnion(projectRef)
        });
      }

      this.router.navigate(['/project', docRef.id]);
      if (formData.status === 'draft') {
        alert('Draft saved successfully!');
        // Stay on the form page for drafts
      } else {
        alert('Form submitted successfully!');
        this.router.navigate(['/project', formData.id || docRef.id]);
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Something went wrong.');
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent) {
    event.preventDefault();
    event.returnValue = '';
  }
  submittedValue = false;

  canDeactivate(): boolean {
    if (!this.submittedValue) {
      return confirm('Are you sure? Changes will be lost.');
    }
    return true;
  }
}
